import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api";

const LINES = ["COMING", "SOON"];

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email, role: "supporter" });
      toast.success("You're on the list!", { description: "We'll email you the moment we launch." });
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080C] bg-grid flex flex-col items-center justify-center px-4 relative overflow-hidden" data-testid="coming-soon-page">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 30%, rgba(139,92,246,0.18), transparent 65%)" }} />

      <div className="relative text-center max-w-2xl w-full">
        <motion.img
          src="/logo.webp"
          alt="G.A.M.E. Mentorship and Training Inc. logo"
          className="h-28 sm:h-36 w-auto mx-auto rounded-sm ring-1 ring-[#FFB800]/60"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="coming-soon-logo"
        />

        <div className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]" data-testid="coming-soon-org">
          G.A.M.E. Mentorship & Training Inc.
        </div>

        <h1 className="mt-4 font-heading font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-7xl text-[#F4F4F6]" data-testid="coming-soon-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-outline-kinetic" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-6 font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#A1A1B3]"
          data-testid="coming-soon-tagline"
        >
          Training · Mentorship · Education · Performance · Lifestyle
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-4 text-sm sm:text-base text-[#A1A1B3] leading-relaxed"
        >
          We're putting the finishing touches on the full experience. Leave your email and be first in the game.
        </motion.p>

        <motion.form
          onSubmit={subscribe}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          data-testid="coming-soon-form"
        >
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="coming-soon-email-input"
            className="flex-1 bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="coming-soon-submit"
            className="bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-sm px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-[#FFB800] active:scale-95 disabled:opacity-50 transition-all duration-200"
          >
            {busy ? "..." : "Notify Me"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
        >
          <a href="mailto:jmizell0057@gmail.com" data-testid="coming-soon-email-link" className="flex items-center gap-2 font-mono text-xs tracking-[0.15em] text-[#A1A1B3] hover:text-[#FFB800] transition-colors">
            <Mail className="w-4 h-4 text-[#8B5CF6]" /> jmizell0057@gmail.com
          </a>
          <a href="tel:+14782313286" data-testid="coming-soon-phone-link" className="flex items-center gap-2 font-mono text-xs tracking-[0.15em] text-[#A1A1B3] hover:text-[#FFB800] transition-colors">
            <Phone className="w-4 h-4 text-[#8B5CF6]" /> (478) 231-3286
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#62627A]">
        © 2026 G.A.M.E. Mentorship and Training Inc.
      </div>
    </div>
  );
}
